import "./App.scss";
import routes from "./Routes/AllRoutes";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import LoadingScreen from "./Shared/LoadingScreen";
import { PublicRoute } from "./Routes/PublicRoutes";
import { PrivateRoute } from "./Routes/PrivateRoutes";
import { useSelector } from "react-redux";
import GeneralLayout from "./Pages/Layout/GeneralLayout";
import React, { Suspense, useEffect, useState, useRef } from "react";
import SwitchPracticeModal from "./Shared/Modals/SwitchPracticeModal";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import useLogout from "./hooks/useLogout";
import {
  LAST_ACTIVITY_KEY,
  readLastActivity,
  writeLastActivity,
} from "./utils/lastActivity";
import { sessionDebugEnabled } from "./utils/sessionDebug";
import { IDLE_TIMEOUT_MS } from "./utils/idleTimeout";

const ACTIVITY_WRITE_THROTTLE_MS = 5000;

const idleLog = (event, details) => {
  if (!sessionDebugEnabled()) return;
  console.info(`[idle] ${new Date().toISOString()} ${event}`, details ?? "");
};

const isIdleExpired = () => {
  const last = readLastActivity();
  return Date.now() - last >= IDLE_TIMEOUT_MS;
};

function App() {
  const { user } = useSelector((state) => state.user);
  const [isShowSwitchPracticeModal, setIsShowSwitchPracticeModal] =
    useState(false);
  const timerRef = useRef(null);
  const userRef = useRef(user);
  const lastActivityLogRef = useRef(0);
  const lastActivityWriteRef = useRef(0);
  const handleLogout = useLogout();

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const scheduleIdleCheck = (delay) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (!userRef.current) return;
      const idleFor = Date.now() - readLastActivity();
      if (idleFor >= IDLE_TIMEOUT_MS) {
        idleLog(
          "IDLE TIMEOUT: no activity in ANY tab -> POST /auth/logout",
          { idleSeconds: Math.round(idleFor / 1000) }
        );
        handleLogout();
      } else {
        idleLog("idle check: recent activity in another tab, staying logged in", {
          lastActivitySecondsAgo: Math.round(idleFor / 1000),
        });
        scheduleIdleCheck(IDLE_TIMEOUT_MS - idleFor);
      }
    }, delay);
  };

  const resetTimer = (eventType) => {
    if (userRef.current && isIdleExpired()) {
      idleLog("activity after idle limit exceeded -> POST /auth/logout", {
        event: eventType ?? "init",
        lastActivitySecondsAgo: Math.round(
          (Date.now() - readLastActivity()) / 1000,
        ),
      });
      handleLogout();
      return;
    }
    const now = Date.now();
    if (now - lastActivityWriteRef.current > ACTIVITY_WRITE_THROTTLE_MS) {
      lastActivityWriteRef.current = now;
      writeLastActivity(now);
    }
    const isContinuousEvent =
      eventType === "mousemove" ||
      eventType === "scroll" ||
      eventType === "keydown";
    if (!isContinuousEvent || now - lastActivityLogRef.current > 5000) {
      lastActivityLogRef.current = now;
      idleLog("activity-detected, idle timer reset", {
        event: eventType ?? "init",
        logoutAfterMinutes: IDLE_TIMEOUT_MS / 60000,
      });
    }
    scheduleIdleCheck(IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    if (user && user?.defaultPractice === null) {
      setIsShowSwitchPracticeModal(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    if (isIdleExpired()) {
      idleLog("session restored after idle limit exceeded -> POST /auth/logout", {
        lastActivitySecondsAgo: Math.round(
          (Date.now() - readLastActivity()) / 1000,
        ),
      });
      handleLogout();
      return;
    }

    const events = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "scroll",
    ];
    const resetActivity = (event) => resetTimer(event.type);
    const onStorageActivity = (event) => {
      if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
        idleLog("activity-detected in ANOTHER tab (storage event)", {
          lastActivityAt: new Date(parseInt(event.newValue, 10)).toISOString(),
        });
      }
    };
    const onWake = () => {
      if (document.visibilityState === "visible" && isIdleExpired()) {
        idleLog("tab woke up after idle limit exceeded -> POST /auth/logout", {
          lastActivitySecondsAgo: Math.round(
            (Date.now() - readLastActivity()) / 1000,
          ),
        });
        handleLogout();
      }
    };
    events.forEach((event) => window.addEventListener(event, resetActivity));
    window.addEventListener("storage", onStorageActivity);
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    resetTimer();
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetActivity),
      );
      window.removeEventListener("storage", onStorageActivity);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSwitchPracticeModalToggle = () =>
    setIsShowSwitchPracticeModal(!isShowSwitchPracticeModal);

  return (
    <React.Fragment>
      <ToastContainer />
      <Router>
        <Routes>
          {routes.map((route, index) => {
            const Component = route.component;
            return (
              <Route
                key={index}
                path={route.path}
                exact={route.exact}
                element={
                  <>
                    <GeneralLayout
                      isPublic={route.isPublic}
                      isGuest={route.isGuest}
                      isPrivate={route.isPrivate}
                      isAuth={route.isAuth}
                      theme={route.theme ?? "black"}
                    >
                      <Suspense fallback={<LoadingScreen />}>
                        {!route.isPublic ? (
                          <PrivateRoute
                            props={route}
                            role={route?.role}
                            Component={Component}
                          />
                        ) : (
                          <PublicRoute
                            props={route}
                            role={route?.role}
                            Component={Component}
                          />
                        )}
                      </Suspense>
                    </GeneralLayout>
                  </>
                }
              />
            );
          })}
        </Routes>
      </Router>

      <SwitchPracticeModal
        isOpen={isShowSwitchPracticeModal}
        toggle={handleSwitchPracticeModalToggle}
      />
    </React.Fragment>
  );
}

export default App;

// Adding a change for testing the CI pipeline